import { useEffect } from 'react';
import {
  updateObjByDotNotation,
  getMarketValueVolume,
  getProtectionValue,
} from '../utils';
import { IEditingTradingFields, IInstrument, IRealPrice } from '../types';
import { IGetInstrumentOutput } from './useGetInstrument';
import { formatByPipSize, getMaxDigitWithDot } from '@trader/utils';

export const useRealTimeChanges = (
  updateInstrument: IGetInstrumentOutput['updateInstrument'],
  realTimePrice: IRealPrice,
  isEditedField: IEditingTradingFields,
  instrument: IInstrument
): void => {
  useEffect(() => {
    updateInstrument(prev =>
      updateObjByDotNotation(
        prev,
        'market.valueVolume',
        getMarketValueVolume(
          prev.market.billType,
          +realTimePrice.ask,
          +realTimePrice.bid,
          prev.market.amountVolume,
          prev.general.pipSize
        )
      )
    );
  }, [realTimePrice]);

  useEffect(() => {
    !isEditedField.stopLoss &&
      updateInstrument(prev =>
        updateObjByDotNotation(
          prev,
          'market.protection.lossValue',
          getProtectionValue(
            prev.market.billType,
            realTimePrice.ask,
            realTimePrice.bid,
            getMaxDigitWithDot(prev.general.pipSize),
            prev.general.pipSize,
            prev.market.protection.stopLossPips
          ).loss
        )
      );
  }, [
    realTimePrice,
    instrument.market.protection.stopLossPips,
    isEditedField.stopLoss,
    instrument.market.billType,
  ]);

  useEffect(() => {
    !isEditedField.takeProfit &&
      updateInstrument(prev =>
        updateObjByDotNotation(
          prev,
          'market.protection.profitValue',
          getProtectionValue(
            prev.market.billType,
            realTimePrice.ask,
            realTimePrice.bid,
            getMaxDigitWithDot(prev.general.pipSize),
            prev.general.pipSize,
            prev.market.protection.profitPips
          ).profit
        )
      );
  }, [
    realTimePrice,
    instrument.market.protection.profitPips,
    isEditedField.takeProfit,
    instrument.market.billType,
  ]);

  useEffect(() => {
    const difference =
      instrument.general.stopsLevel * instrument.general.pipSize;

    const buyValue = Number(realTimePrice.bid) + difference;
    !isEditedField.limitBuy &&
      updateInstrument(prev =>
        updateObjByDotNotation(
          prev,
          'limit.buyValue',
          formatByPipSize(buyValue, instrument.general.pipSize)
        )
      );
  }, [realTimePrice, isEditedField.limitBuy, instrument.limit.billType]);

  useEffect(() => {
    const difference =
      instrument.general.stopsLevel * instrument.general.pipSize;
    const sellValue = Number(realTimePrice.ask) - difference;

    !isEditedField.limitSell &&
      updateInstrument(prev =>
        updateObjByDotNotation(
          prev,
          'limit.sellValue',
          formatByPipSize(sellValue, instrument.general.pipSize)
        )
      );
  }, [realTimePrice, isEditedField.limitSell, instrument.limit.billType]);
};
